import React, { useState, useEffect } from 'react';
import StockService from '../../../services/stockService';
import './MergedFinancialData.css';

const MergedFinancialData = ({ symbol }) => {
  const [stockData, setStockData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!symbol) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch both stock info and metrics
        const [stockInfo, mockMetrics] = await Promise.all([
          StockService.getStockInfo(symbol),
          // For now using mock metrics - replace with actual API call when available
          Promise.resolve({
            pe: 25.5,
            pb: 3.2,
            ps: 8.1,
            dividend: 0.85,
            dividendYield: 2.1,
            beta: 1.15,
            eps: 5.89,
            revenue: 394328000000,
            profit: 96995000000,
            debt: 287912000000,
            cash: 48084000000
          })
        ]);
        
        setStockData(stockInfo);
        setMetrics(mockMetrics);
      } catch (err) {
        console.error('❌ Error fetching financial data:', err);
        setError('Failed to fetch financial data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [symbol]);

  if (loading) {
    return (
      <div className="merged-financial-data">
        <div className="loading-placeholder">
          <div className="loading-spinner">⏳</div>
          <p>Loading financial data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="merged-financial-data">
        <div className="error-message">
          <span>❌</span>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!stockData || !metrics) {
    return (
      <div className="merged-financial-data">
        <div className="no-data">
          <p>No financial data available</p>
        </div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return `$${value.toLocaleString()}`;
  };

  const getTrendIndicator = (value, isPositive = true) => {
    if (isPositive) {
      return value > 0 ? '↗️' : value < 0 ? '↘️' : '→';
    }
    return value > 0 ? '↘️' : value < 0 ? '↗️' : '→';
  };

  return (
    <div className="merged-financial-data">
      <div className="financial-header">
        <h3>Financial Overview</h3>
        <p>Comprehensive financial metrics and company profile for {stockData.symbol}</p>
      </div>

      <div className="financial-grid">
        {/* Column 1: Company Profile & Market Cap */}
        <div className="financial-column">
          <div className="column-header">
            <h4>Company Profile</h4>
          </div>
          <div className="profile-section">
            <div className="profile-item">
              <span className="profile-label">Market Cap:</span>
              <span className="profile-value">
                {stockData.market_cap ? 
                  `$${(stockData.market_cap / 1e9).toFixed(2)}B` : 'N/A'
                }
              </span>
            </div>
            <div className="profile-item">
              <span className="profile-label">Sector:</span>
              <span className="profile-value">{stockData.sector || 'N/A'}</span>
            </div>
            <div className="profile-item">
              <span className="profile-label">Industry:</span>
              <span className="profile-value">{stockData.industry || 'N/A'}</span>
            </div>
            <div className="profile-item">
              <span className="profile-label">Country:</span>
              <span className="profile-value">{stockData.country || 'N/A'}</span>
            </div>
            <div className="profile-item">
              <span className="profile-label">Employees:</span>
              <span className="profile-value">
                {stockData.employees ? stockData.employees.toLocaleString() : 'N/A'}
              </span>
            </div>
            {stockData.website && (
              <div className="profile-item">
                <span className="profile-label">Website:</span>
                <a 
                  href={stockData.website} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="profile-link"
                >
                  {stockData.website}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Column 2: Valuation Ratios */}
        <div className="financial-column">
          <div className="column-header">
            <h4>Valuation Ratios</h4>
          </div>
          <div className="ratios-section">
            <div className="ratio-item">
              <span className="ratio-label">P/E Ratio:</span>
              <span className="ratio-value">
                {stockData.pe_ratio ? stockData.pe_ratio.toFixed(2) : 'N/A'}
              </span>
              <span className="ratio-trend">
                {getTrendIndicator(stockData.pe_ratio || 0, false)}
              </span>
            </div>
            <div className="ratio-item">
              <span className="ratio-label">Forward P/E:</span>
              <span className="ratio-value">
                {stockData.forward_pe ? stockData.forward_pe.toFixed(2) : 'N/A'}
              </span>
              <span className="ratio-trend">
                {getTrendIndicator(stockData.forward_pe || 0, false)}
              </span>
            </div>
            <div className="ratio-item">
              <span className="ratio-label">P/B Ratio:</span>
              <span className="ratio-value">
                {stockData.price_to_book ? stockData.price_to_book.toFixed(2) : 'N/A'}
              </span>
              <span className="ratio-trend">
                {getTrendIndicator(stockData.price_to_book || 0, false)}
              </span>
            </div>
            <div className="ratio-item">
              <span className="ratio-label">P/S Ratio:</span>
              <span className="ratio-value">
                {metrics.ps ? metrics.ps.toFixed(2) : 'N/A'}
              </span>
              <span className="ratio-trend">
                {getTrendIndicator(metrics.ps || 0, false)}
              </span>
            </div>
            <div className="ratio-item">
              <span className="ratio-label">Debt to Equity:</span>
              <span className="ratio-value">
                {stockData.debt_to_equity ? stockData.debt_to_equity.toFixed(2) : 'N/A'}
              </span>
              <span className="ratio-trend">
                {getTrendIndicator(stockData.debt_to_equity || 0, false)}
              </span>
            </div>
          </div>
        </div>

        {/* Column 3: Financial Performance */}
        <div className="financial-column">
          <div className="column-header">
            <h4>Financial Performance</h4>
          </div>
          <div className="performance-section">
            <div className="performance-item">
              <span className="performance-label">EPS:</span>
              <span className="performance-value">
                ${metrics.eps ? metrics.eps.toFixed(2) : 'N/A'}
              </span>
              <span className="performance-trend">
                {getTrendIndicator(metrics.eps || 0)}
              </span>
            </div>
            <div className="performance-item">
              <span className="performance-label">Dividend Yield:</span>
              <span className="performance-value">
                {metrics.dividendYield ? metrics.dividendYield.toFixed(2) + '%' : 'N/A'}
              </span>
              <span className="performance-trend">
                {getTrendIndicator(metrics.dividendYield || 0)}
              </span>
            </div>
            <div className="performance-item">
              <span className="performance-label">Beta:</span>
              <span className="performance-value">
                {metrics.beta ? metrics.beta.toFixed(2) : 'N/A'}
              </span>
              <span className="performance-trend">→</span>
            </div>
            <div className="performance-item">
              <span className="performance-label">Revenue:</span>
              <span className="performance-value">
                {formatCurrency(metrics.revenue)}
              </span>
              <span className="performance-trend">
                {getTrendIndicator(metrics.revenue || 0)}
              </span>
            </div>
            <div className="performance-item">
              <span className="performance-label">Net Profit:</span>
              <span className="performance-value">
                {formatCurrency(metrics.profit)}
              </span>
              <span className="performance-trend">
                {getTrendIndicator(metrics.profit || 0)}
              </span>
            </div>
            <div className="performance-item">
              <span className="performance-label">Cash & Equiv:</span>
              <span className="performance-value">
                {formatCurrency(metrics.cash)}
              </span>
              <span className="performance-trend">
                {getTrendIndicator(metrics.cash || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MergedFinancialData;
